import React, { useState } from 'react';
import { 
  Users, 
  UserPlus, 
  UserMinus, 
  Shield, 
  ShieldCheck, 
  ChevronRight, 
  Search,
  Filter,
  MoreVertical,
  ArrowRightLeft
} from 'lucide-react';

const ManagerAllocation = ({ users: initialUsers, onUpdateUsers }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [users, setUsers] = useState(initialUsers);

  const managers = users.filter(u => u.userRole === 'Management' || u.userRole === 'Admin');
  
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.userRole === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleRoleToggle = (userId) => {
    const updatedUsers = users.map(user => {
      if (user.id === userId) {
        if (user.userRole === 'Admin') return user; // Don't change admin role here
        const newRole = user.userRole === 'Management' ? 'Normal' : 'Management';
        return { ...user, userRole: newRole };
      }
      return user;
    });
    setUsers(updatedUsers);
    onUpdateUsers(updatedUsers);
  };

  const handleManagerChange = (userId, managerId) => {
    const updatedUsers = users.map(user => {
      if (user.id === userId) {
        return { ...user, managerId: managerId === 'none' ? null : managerId };
      }
      return user;
    });
    setUsers(updatedUsers);
    onUpdateUsers(updatedUsers);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Manager Allocation</h2>
          <p className="text-slate-500 text-sm font-medium">Control roles and organize project teams.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input 
            type="text" 
            placeholder="Search by name or email..." 
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-2">
          {['all', 'Management', 'Normal'].map((role) => (
            <button
              key={role}
              onClick={() => setRoleFilter(role)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                roleFilter === role 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {role === 'all' ? 'All Roles' : role}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">User Info</th>
              <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Current Role</th>
              <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Assigned Manager</th>
              <th className="text-right px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredUsers.map((user) => {
              const userManager = managers.find(m => m.id === user.managerId);
              
              return (
                <tr key={user.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center text-blue-600 font-bold border border-blue-100 shadow-sm">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-800">{user.name}</div>
                        <div className="text-xs text-slate-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                      user.userRole === 'Admin' ? 'bg-purple-50 text-purple-600' :
                      user.userRole === 'Management' ? 'bg-blue-50 text-blue-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {user.userRole === 'Admin' ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                      {user.userRole}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {user.userRole === 'Admin' ? (
                      <span className="text-xs text-slate-400 font-mediumitalic italic">No allocation needed</span>
                    ) : (
                      <select
                        className="bg-slate-50 border-none rounded-lg text-xs font-medium px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none w-full max-w-[180px] transition-all cursor-pointer"
                        value={user.managerId || 'none'}
                        onChange={(e) => handleManagerChange(user.id, e.target.value)}
                      >
                        <option value="none">No Manager Assigned</option>
                        {managers
                          .filter(m => m.id !== user.id) // Can't manage self
                          .map(manager => (
                            <option key={manager.id} value={manager.id}>
                              {manager.name} ({manager.company})
                            </option>
                          ))
                        }
                      </select>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {user.userRole !== 'Admin' && (
                      <button
                        onClick={() => handleRoleToggle(user.id)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                          user.userRole === 'Management' 
                          ? 'text-red-600 hover:bg-red-50' 
                          : 'text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <ArrowRightLeft className="w-3 h-3" />
                        {user.userRole === 'Management' ? 'Demote to Normal' : 'Promote to Management'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-medium">No users found matching your search.</p>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex gap-4">
          <div className="bg-blue-600 rounded-xl p-3 h-fit shadow-lg shadow-blue-200">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="font-bold text-blue-900 mb-1 text-lg">Administrative Control</h4>
            <p className="text-blue-700 text-sm leading-relaxed max-w-2xl font-medium">
              Promoting a user to <strong>Management</strong> grants them access to Team Insights and Approvals. 
              Allocating a team member to a manager ensures that the manager's "Team Insight" dashboard correctly displays their direct reports' data.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerAllocation;
